import React, { useEffect, useCallback, useContext, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation as useRouterLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, AuthContext } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import Navbar from './components/Navbar';
import NavbarOwner from './components/NavbarOwner';
import ErrorBoundary, { initGlobalErrorHandler } from './components/ErrorBoundary';
import PrivateRoute from './components/PrivateRoute';
import OwnerPrivateRoute from './components/OwnerPrivateRoute';
import TeamPrivateRoute from './components/TeamPrivateRoute';

// Loading spinner for lazy pages
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
  </div>
);

// Retry lazy import on chunk load failure (after deploy)
const lazyRetry = (fn) => lazy(() => fn().catch(() => {
  window.location.reload();
  return new Promise(() => {});
}));

// Lazy loaded pages
const HomePage = lazyRetry(() => import('./pages/HomePage'));
const LoginPage = lazyRetry(() => import('./pages/LoginPage'));
const RegisterPage = lazyRetry(() => import('./pages/RegisterPage'));
const HealthcareRegisterPage = lazyRetry(() => import('./pages/HealthcareRegisterPage'));
const SalonRegisterPage = lazyRetry(() => import('./pages/SalonRegisterPage'));
const SalonDashboard = lazyRetry(() => import('./pages/SalonDashboard'));
const SalonLandingPage = lazyRetry(() => import('./pages/SalonLandingPage'));
const TailoringLandingPage = lazyRetry(() => import('./pages/TailoringLandingPage'));
const TailoringRegisterPage = lazyRetry(() => import('./pages/TailoringRegisterPage'));
const CashierRegisterPage = lazyRetry(() => import('./pages/CashierRegisterPage'));
const TailoringDashboard = lazyRetry(() => import('./pages/TailoringDashboard'));
const DashboardPage = lazyRetry(() => import('./pages/DashboardPage'));
const AdminDashboardPage = lazyRetry(() => import('./pages/AdminDashboardPage'));
const ProjectsPage = lazyRetry(() => import('./pages/ProjectsPage'));
const InvoicesPage = lazyRetry(() => import('./pages/InvoicesPage'));
const InventoryPage = lazyRetry(() => import('./pages/InventoryPage'));
const ProjectDetailsPage = lazyRetry(() => import('./pages/ProjectDetailsPage'));
const ProjectCostCalculatorPage = lazyRetry(() => import('./pages/ProjectCostCalculatorPage'));
const SettingsPage = lazyRetry(() => import('./pages/SettingsPage'));
const TeamPage = lazyRetry(() => import('./pages/TeamPage'));
const ProfilePage = lazyRetry(() => import('./pages/ProfilePage'));
const ReportsPage = lazyRetry(() => import('./pages/ReportsPage'));
const AdminSetupPage = lazyRetry(() => import('./pages/AdminSetupPage'));
const AppointmentsPage = lazyRetry(() => import('./pages/AppointmentsPage'));
const SalesDashboardPage = lazyRetry(() => import('./pages/SalesDashboardPage'));
const QuotationsPage = lazyRetry(() => import('./pages/QuotationsPage'));
const CRMPage = lazyRetry(() => import('./pages/CRMPage'));
const ManufacturingPage = lazyRetry(() => import('./pages/ManufacturingPage'));
const AccountingPage = lazyRetry(() => import('./pages/AccountingPage'));
const HRPage = lazyRetry(() => import('./pages/HRPage'));
const NotFoundPage = lazyRetry(() => import('./pages/NotFoundPage'));
const ForgotPasswordPage = lazyRetry(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazyRetry(() => import('./pages/ResetPasswordPage'));
const VerifyEmailPage = lazyRetry(() => import('./pages/VerifyEmailPage'));
const PaymentSuccessPage = lazyRetry(() => import('./pages/PaymentSuccessPage'));
const PaymentCancelPage = lazyRetry(() => import('./pages/PaymentCancelPage'));
const SubscriptionPage = lazyRetry(() => import('./pages/SubscriptionPage'));
const OwnerLoginPage = lazyRetry(() => import('./pages/OwnerLoginPage'));
const OwnerDashboard = lazyRetry(() => import('./pages/OwnerDashboard'));
const TechAdminLoginPage = lazyRetry(() => import('./pages/TechAdminLoginPage'));
const TechAdminDashboardPage = lazyRetry(() => import('./pages/TechAdminDashboardPage'));
const HospitalsPage = lazyRetry(() => import('./pages/HospitalsPage'));
const ClinicsPage = lazyRetry(() => import('./pages/ClinicsPage'));
const DoctorsPage = lazyRetry(() => import('./pages/DoctorsPage'));
const HealthcarePage = lazyRetry(() => import('./pages/HealthcarePage'));
const HealthcareStaffPage = lazyRetry(() => import('./pages/HealthcareStaffPage'));
const ClinicPatientsPage = lazyRetry(() => import('./pages/ClinicPatientsPage'));
const MedicalBillingPage = lazyRetry(() => import('./pages/MedicalBillingPage'));
const HealthcareReportsPage = lazyRetry(() => import('./pages/HealthcareReportsPage'));
const PharmacyPage = lazyRetry(() => import('./pages/PharmacyPage'));
const LaboratoryPage = lazyRetry(() => import('./pages/LaboratoryPage'));
const EmailManagementPage = lazyRetry(() => import('./pages/EmailManagementPage'));
const WhatsAppPage = lazyRetry(() => import('./pages/WhatsAppPage'));
const LegalPages = lazyRetry(() => import('./pages/LegalPages'));
const TermsPage = lazy(() => import('./pages/LegalPages').then(m => ({ default: m.TermsPage })));
const PrivacyPage = lazy(() => import('./pages/LegalPages').then(m => ({ default: m.PrivacyPage })));
const AboutPage = lazyRetry(() => import('./pages/AboutPage'));
const ContactPage = lazyRetry(() => import('./pages/ContactPage'));
const TeamLoginPage = lazyRetry(() => import('./pages/TeamLoginPage'));
const TeamDashboard = lazyRetry(() => import('./pages/TeamDashboard'));
const BookingPage = lazyRetry(() => import('./pages/BookingPage'));
const CashierPage = lazyRetry(() => import('./pages/CashierPage'));
const CashierDashboardPage = lazyRetry(() => import('./pages/CashierDashboardPage'));
const ExpensesPage = lazyRetry(() => import('./pages/ExpensesPage'));
const PurchasesPage = lazyRetry(() => import('./pages/PurchasesPage'));
const StocktakePage = lazyRetry(() => import('./pages/StocktakePage'));
const PricingPage = lazyRetry(() => import('./pages/PricingPage'));
const CashierLandingPage = lazyRetry(() => import('./pages/CashierLandingPage'));

// On-premise mode detection
const IS_ON_PREMISE = process.env.REACT_APP_ON_PREMISE === 'true';

const TRACKING_API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// مكون تتبع الصفحات
function PageTracker() {
  const location = useRouterLocation();

  const trackVisit = useCallback((page) => {
    try {
      const visitorId = localStorage.getItem('visitor_id') || (() => {
        const id = Math.random().toString(36).substring(2) + Date.now().toString(36);
        localStorage.setItem('visitor_id', id);
        return id;
      })();

      fetch(`${TRACKING_API}/visits/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          page,
          referrer: document.referrer || '',
          visitorId,
        }),
      }).catch(() => {});
    } catch { /* لا نريد أي خطأ يؤثر على تجربة المستخدم */ }
  }, []);

  useEffect(() => {
    const page = location.pathname.replace(/^\//, '') || 'home';
    trackVisit(page);
  }, [location.pathname, trackVisit]);

  return null;
}

// تنبيه انتهاء الجلسة (تسجيل دخول من جهاز آخر)
function SessionExpiredAlert() {
  const { sessionExpired, setSessionExpired } = useContext(AuthContext);
  if (!sessionExpired) return null;
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm" dir="rtl">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md mx-4 text-center">
        <div className="text-5xl mb-4">🔒</div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">انتهت الجلسة</h2>
        <p className="text-gray-600 mb-6">{sessionExpired}</p>
        <button
          onClick={() => { setSessionExpired(false); window.location.href = '/login'; }}
          className="px-8 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition"
        >
          تسجيل الدخول مرة أخرى
        </button>
      </div>
    </div>
  );
}

function App() {
  const location = window.location;
  // إخفاء الـ Navbar في صفحات المالك والأدمن التقني
  const isOwnerPage = location.pathname.startsWith('/owner-login') || location.pathname.startsWith('/owner-dashboard') || location.pathname.startsWith('/owner-settings');
  const isTechAdminPage = location.pathname.startsWith('/tech-admin');
  const isTeamPage = location.pathname.startsWith('/team-login') || location.pathname.startsWith('/team-dashboard');
  const isBookingPage = location.pathname.startsWith('/book/');
  const isAuthPage = location.pathname === '/login' || location.pathname === '/register' || location.pathname.startsWith('/register/') || location.pathname === '/forgot-password' || location.pathname.startsWith('/reset-password');
  const hideNavbar = isOwnerPage || isTechAdminPage || isTeamPage || isBookingPage || isAuthPage;

  // تفعيل التقاط الأخطاء العامة
  useEffect(() => {
    initGlobalErrorHandler();
  }, []);

  return (
    <ErrorBoundary>
    <Router>
      <PageTracker />
      <AuthProvider>
        <ThemeProvider>
          <div className="min-h-screen bg-gray-50">
            {!hideNavbar && !IS_ON_PREMISE && <Navbar />}
            {isOwnerPage && <NavbarOwner />}
            <Toaster position="top-right" />
            <SessionExpiredAlert />
            <Suspense fallback={<PageLoader />}>
            <Routes>
              {IS_ON_PREMISE ? (
                <>
                  {/* On-Premise: كاشير فقط */}
                  <Route path="/" element={<Navigate to="/login" replace />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/cashier" element={<PrivateRoute><CashierPage /></PrivateRoute>} />
                  <Route path="/expenses" element={<PrivateRoute><ExpensesPage /></PrivateRoute>} />
                  <Route path="/purchases" element={<PrivateRoute><PurchasesPage /></PrivateRoute>} />
                  <Route path="/stocktake" element={<PrivateRoute><StocktakePage /></PrivateRoute>} />
                  <Route path="/inventory" element={<PrivateRoute><InventoryPage /></PrivateRoute>} />
                  <Route path="/reports" element={<PrivateRoute><ReportsPage /></PrivateRoute>} />
                  <Route path="/hr" element={<PrivateRoute><HRPage /></PrivateRoute>} />
                  <Route path="/accounting" element={<PrivateRoute><AccountingPage /></PrivateRoute>} />
                  <Route path="/settings" element={<PrivateRoute><SettingsPage /></PrivateRoute>} />
                  <Route path="/profile" element={<PrivateRoute><ProfilePage /></PrivateRoute>} />
                  <Route path="/dashboard" element={<Navigate to="/cashier" replace />} />
                  <Route path="*" element={<Navigate to="/login" replace />} />
                </>
              ) : (
                <>
              <Route path="/" element={<HomePage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/register/healthcare" element={<HealthcareRegisterPage />} />
              <Route path="/register/salon" element={<SalonRegisterPage />} />
              <Route path="/register/tailoring" element={<TailoringRegisterPage />} />
              <Route path="/register/cashier" element={<CashierRegisterPage />} />
              <Route path="/salon" element={<PrivateRoute><SalonDashboard /></PrivateRoute>} />
              <Route path="/tailoring-dashboard" element={<PrivateRoute><TailoringDashboard /></PrivateRoute>} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/payment-success" element={<PaymentSuccessPage />} />
              <Route path="/payment-cancel" element={<PaymentCancelPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/dashboard" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
              <Route path="/projects" element={<PrivateRoute><ProjectsPage /></PrivateRoute>} />
              <Route path="/projects/:id" element={<PrivateRoute><ProjectDetailsPage /></PrivateRoute>} />
              <Route path="/project-costs" element={<PrivateRoute><ProjectCostCalculatorPage /></PrivateRoute>} />
              <Route path="/invoices" element={<PrivateRoute><InvoicesPage /></PrivateRoute>} />
              <Route path="/inventory" element={<PrivateRoute><InventoryPage /></PrivateRoute>} />
              <Route path="/settings" element={<PrivateRoute><SettingsPage /></PrivateRoute>} />
              <Route path="/subscription" element={<PrivateRoute><SubscriptionPage /></PrivateRoute>} />
              <Route path="/team" element={<PrivateRoute><TeamPage /></PrivateRoute>} />
              <Route path="/profile" element={<PrivateRoute><ProfilePage /></PrivateRoute>} />
              <Route path="/reports" element={<PrivateRoute><ReportsPage /></PrivateRoute>} />
              <Route path="/healthcare-reports" element={<PrivateRoute><HealthcareReportsPage /></PrivateRoute>} />
              <Route path="/admin-setup" element={<PrivateRoute><AdminSetupPage /></PrivateRoute>} />
              <Route path="/appointments" element={<PrivateRoute><AppointmentsPage /></PrivateRoute>} />
              <Route path="/sales" element={<PrivateRoute><SalesDashboardPage /></PrivateRoute>} />
              <Route path="/quotations" element={<PrivateRoute><QuotationsPage /></PrivateRoute>} />
              <Route path="/crm" element={<PrivateRoute><CRMPage /></PrivateRoute>} />
              <Route path="/manufacturing" element={<PrivateRoute><ManufacturingPage /></PrivateRoute>} />
              <Route path="/accounting" element={<PrivateRoute><AccountingPage /></PrivateRoute>} />
              <Route path="/hr" element={<PrivateRoute><HRPage /></PrivateRoute>} />
              <Route path="/cashier-dashboard" element={<PrivateRoute><CashierDashboardPage /></PrivateRoute>} />
              <Route path="/cashier" element={<PrivateRoute><CashierPage /></PrivateRoute>} />
              <Route path="/expenses" element={<PrivateRoute><ExpensesPage /></PrivateRoute>} />
              <Route path="/purchases" element={<PrivateRoute><PurchasesPage /></PrivateRoute>} />
              <Route path="/stocktake" element={<PrivateRoute><StocktakePage /></PrivateRoute>} />
              <Route path="/admin" element={<PrivateRoute><AdminDashboardPage /></PrivateRoute>} />
              <Route path="/owner" element={<Navigate to="/owner-login" replace />} />
              <Route path="/owner-login" element={<OwnerLoginPage />} />
              <Route path="/owner-dashboard" element={<OwnerPrivateRoute><OwnerDashboard /></OwnerPrivateRoute>} />
              <Route path="/owner-settings" element={<OwnerPrivateRoute><OwnerDashboard tabDefault="settings" /></OwnerPrivateRoute>} />
              <Route path="/tech-admin-login" element={<TechAdminLoginPage />} />
              <Route path="/tech-admin-dashboard" element={<TechAdminDashboardPage />} />
              <Route path="/team-login" element={<TeamLoginPage />} />
              <Route path="/team-dashboard" element={<TeamPrivateRoute><TeamDashboard /></TeamPrivateRoute>} />
              <Route path="/hospitals" element={<PrivateRoute><HospitalsPage /></PrivateRoute>} />
              <Route path="/clinics" element={<PrivateRoute><ClinicsPage /></PrivateRoute>} />
              <Route path="/doctors" element={<PrivateRoute><DoctorsPage /></PrivateRoute>} />
              <Route path="/healthcare-staff" element={<PrivateRoute><HealthcareStaffPage /></PrivateRoute>} />
              <Route path="/clinic-patients" element={<PrivateRoute><ClinicPatientsPage /></PrivateRoute>} />
              <Route path="/medical-billing" element={<PrivateRoute><MedicalBillingPage /></PrivateRoute>} />
              <Route path="/pharmacy" element={<PrivateRoute><PharmacyPage /></PrivateRoute>} />
              <Route path="/laboratory" element={<PrivateRoute><LaboratoryPage /></PrivateRoute>} />
              <Route path="/emails" element={<PrivateRoute><EmailManagementPage /></PrivateRoute>} />
              <Route path="/whatsapp" element={<PrivateRoute><WhatsAppPage /></PrivateRoute>} />
              <Route path="/healthcare" element={<HealthcarePage />} />
              <Route path="/salons" element={<SalonLandingPage />} />
              <Route path="/tailoring" element={<TailoringLandingPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/cashier-landing" element={<CashierLandingPage />} />
              <Route path="/book/:orgId" element={<BookingPage />} />
              <Route path="*" element={<NotFoundPage />} />
                </>
              )}
            </Routes>
            </Suspense>
          </div>
        </ThemeProvider>
      </AuthProvider>
    </Router>
    </ErrorBoundary>
  );
}

export default App;
